// eslint-disable-next-line max-classes-per-file
import { normalize } from 'normalizr';
import * as Sentry from '@sentry/react';

import { forOwn, isFunction, isObject, omit } from 'helpers/utils';

import { ERROR_ACTION } from 'constants/error';
import { REQUEST_TYPE } from 'constants/api';

import { createCoreAxiosInstance } from 'createAxiosInstance';

function serialize(params, obj, traditional, scope) {
  let type;
  const array = Array.isArray(obj);
  const hash = isObject(obj);
  obj.forEach((value, key) => {
    type = typeof value;
    if (scope) {
      key = traditional // eslint-disable-line no-param-reassign
        ? scope
        : scope + // eslint-disable-line
          '[' +
          (hash || type === 'object' || type === 'array' ? key : '') +
          ']'; // eslint-disable-line
    }
    // handle data in serializeArray() format
    if (!scope && array) {
      params.add(value.name, value.value);
    } else if (type === 'array' || (!traditional && type === 'object')) {
      // recurse into nested objects
      serialize(params, value, traditional, key);
    } else {
      params.add(key, value);
    }
  });
}

function param(obj, traditional) {
  try {
    let params = []; // eslint-disable-line
    // eslint-disable-next-line
    params.add = function (key, value) {
      // eslint-disable-line
      if (isFunction(value)) value = value(); // eslint-disable-line
      if (value == null) value = ''; // eslint-disable-line
      this.push(escape(key) + '=' + escape(value)); // eslint-disable-line
    };
    serialize(params, obj, traditional); // eslint-disable-line
    return params.join('&').replace(/%20/g, '+'); // eslint-disable-line
  } catch {
    return obj;
  }
}

export function formatError(err) {
  if (err) {
    const responseError = err.errors ? err.errors[0] : { message: err };
    return { errors: [responseError] };
  }

  return { errors: [] };
}

/**
 * @typedef {Object} FormatRequestArgs
 * @property {string} path the request path
 * @property {Record<string, unknown>} [params] params
 * @property {"GET" | "POST"} [method=GET]
 * @property {string} [customHost] custom host URL
 * @property {string} [apiVersion] version of api to be used
 */

/**
 * Formatting function that returns an object formatted for the Fetch API
 * @param {FormatRequestArgs}
 */
export function formatRequest({ path, params, method = 'GET', customHost, apiVersion }) {
  const apiHost = import.meta.env.VITE_API_HOST;
  const vitePackage = import.meta.env.VITE_PACKAGE;

  const endpoint = customHost ? `${customHost}/${path}` : `${apiHost}/${path}`;
  const finalParams = params || {};
  const version = apiVersion || '3.0';
  const headers = {
    Accept: `application/json; version=${version}`,
    'Content-Type': 'application/json; charset=UTF-8',
    'X-CN-App-Info': `${vitePackage}/${import.meta.env.VITE_APP_VERSION}`,
  };

  const credentials = customHost === import.meta.env.VITE_ONBOARDING_API_HOST ? 'omit' : 'include';

  // Short circuit any GET requests
  if (method.toUpperCase() === 'GET') {
    return {
      credentials,
      method,
      headers,
      endpoint: Object.keys(finalParams || {}).length
        ? `${endpoint}?${param(finalParams)}`
        : endpoint,
    };
  }

  return {
    credentials,
    method,
    headers,
    endpoint,
    body: JSON.stringify(finalParams),
  };
}

export function getGenericPath(originalPath) {
  const [basePath, queryString] = originalPath.split('?');
  let genericPath = basePath;

  if (queryString) {
    const params = new URLSearchParams(queryString);
    // remove specific resto/company ids from query params
    params.delete('company_id');
    params.delete('restaurant_id');
    params.delete('companyID');
    params.delete('merchantID');
    // create url again with generic query params
    const genericQuery = params.toString();
    genericPath = genericQuery ? `${genericPath}?${genericQuery}` : genericPath;
  }

  // add leading slash if not already there
  if (!genericPath.startsWith('/')) {
    genericPath = `/${genericPath}`;
  }

  // replace restaurant ids in paths with :id
  genericPath = genericPath.replace(/\/restaurant\/(?:cuboh-)?\d+/, '/restaurant/:id');

  // replace company ids in paths with :id
  genericPath = genericPath.replace(/\/company\/\d+/, '/company/:id');

  // replace merchant ids in paths with :id
  genericPath = genericPath.replace(/\/merchant\/\d+/, '/merchant/:id');

  // replace order ids in paths with :id
  genericPath = genericPath.replace(/\/order\/\d+/, '/order/:id');

  // replace catalog modifier ids in paths with :id
  genericPath = genericPath.replace(/\/modifier\/\d+/, '/modifier/:id');

  // replace catalog item ids in paths with :id
  genericPath = genericPath.replace(/\/item\/\d+(_\d+)?/, '/item/:id');

  // replace uuids in paths with :uuid
  genericPath = genericPath.replace(
    /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g,
    '/:uuid',
  );

  // remove domain if present
  genericPath = genericPath.replace(/^https?:\/\/[^/]+/, '');

  return genericPath;
}

const sendApiSentryError = (path, statusCode, method, payload) => {
  const genericPath = getGenericPath(path);
  const errorGroup = Math.floor(statusCode / 100);

  Sentry.withScope(scope => {
    // group errors by endpoint/path
    scope.setFingerprint([`api-${errorGroup}xx-error`, genericPath]);

    // add additional debugging context
    scope.setExtra('endpoint', path);
    scope.setExtra('status', statusCode);
    scope.setExtra('method', method);

    // show error object directly instead of the nested structure
    if (payload?.errors?.length > 0) {
      scope.setExtra('response', payload.errors[0]);
    } else {
      scope.setExtra('response', payload);
    }

    // add endpoint as a tag so we can filter in Sentry
    scope.setTag('api_endpoint', genericPath);

    Sentry.captureException(
      new (class extends Error {
        constructor() {
          super(`${method} ${genericPath}`);
          this.name = `API ${errorGroup}XX Error`;
        }
      })(),
    );
  });
};

/**
 * @typedef ApiErrorResponse
 * @property {string} message
 * @property {string} code
 */

export class ApiError extends Error {
  /** @arg {ApiErrorResponse} apiErrorResponse */
  constructor(apiErrorResponse) {
    super(apiErrorResponse.message);
    this.code = apiErrorResponse.code;
  }
}

/* @todo `apiRequest` should be updated to throw `ApiError`s so it's consistent but every
 * instance of handling apiRequest errors will have to be updated */
/** @arg {Error | { error: ApiErrorResponse }} apiErrorResponseError */
export function apiRequestErrorToError(apiErrorResponseError) {
  return apiErrorResponseError instanceof Error
    ? apiErrorResponseError
    : new ApiError(apiErrorResponseError.error);
}

/**
 * @typedef {Object} ApiRequestOptions
 *
 * @property {string} path
 * @property {Record<string, unknown>} [params]
 * @property {"GET" | "POST" | "PUT" | "PATCH" | "DELETE"} [method]
 * @property {string} [customHost]
 * @property {string} [serializer]
 * @property {number} [timeout]
 * @property {string} [apiVersion]
 * @property {() => void} [setError]
 * @property {(response: Response | undefined, error: unknown, payload?: unknown) => void} [onError]
 * @property {((result: Response) => boolean) | boolean} [trackErrorsInSentry]
 * @property {AbortController} [controller]
 */

/**
 * @param {ApiRequestOptions}
 * @template T
 * @returns {Promise<
 *  { data: T, error: null } |
 *  { data: null, error: Error | { error: ApiErrorResponse } }
 * >}
 */
export async function apiRequest({
  path,
  params,
  method = REQUEST_TYPE.GET,
  customHost,
  serializer,
  timeout,
  apiVersion,
  trackErrorsInSentry = true,
  setError,
  onError,
  controller,
}) {
  const { endpoint, ...options } = formatRequest({
    path,
    params,
    method,
    customHost,
    apiVersion,
  });

  let result;
  // Hoisted so `onError` can read the parsed body: 5xx replaces the thrown value with a
  // generic Error, which would otherwise discard the API's own message.
  let payload = null;

  try {
    if (timeout) {
      const timeoutController = controller || new AbortController();
      // abort request if no response returned after specified time
      const abortRequest = setTimeout(() => timeoutController.abort(), timeout);

      result = await fetch(endpoint, {
        ...options,
        signal: timeoutController.signal,
      });
      // clean up request abortion if fetch is successful
      clearTimeout(abortRequest);
    } else {
      result = await fetch(endpoint, { ...options, signal: controller?.signal });
    }

    payload = result.status !== 204 ? await result.json() : null;

    // verify request had 2xx status code
    if (result.ok) {
      if (isFunction(serializer)) {
        payload = serializer(payload);
      } else if (isObject(serializer)) {
        payload = normalize(payload, serializer);
      }
    } else {
      if (
        (typeof trackErrorsInSentry === 'boolean' && trackErrorsInSentry) ||
        (typeof trackErrorsInSentry === 'function' && trackErrorsInSentry(result))
      ) {
        sendApiSentryError(path, result.status, method, payload);
      }

      let errorAction = null;
      let errorToThrow = payload;

      // all cases throw an error
      if (result.status >= 500) {
        errorAction = ERROR_ACTION.SHOW_ERROR_MODAL;
        errorToThrow = new Error('Server error occurred. Try again later.');
      } else if (result.status === 403 || result.status === 401) {
        errorAction = ERROR_ACTION.SHOW_UNAUTHORIZED_ERROR;
        errorToThrow = result;
      } else if (result.status === 400) {
        errorAction = null;
      }

      setError?.({ errorAction, hasError: true });
      throw errorToThrow;
    }

    return { error: null, data: payload };
  } catch (err) {
    onError?.(result, err, payload);
    return { data: null, error: err };
  }
}

export async function axiosApiRequest({
  createAxiosInstance = createCoreAxiosInstance,
  method = REQUEST_TYPE.GET,
  path,
  data,
  setError,
  trackErrorsInSentry = true,
}) {
  try {
    const axiosInstance = await createAxiosInstance();
    const { data: responseData } = await axiosInstance.request({
      method,
      url: path,
      data,
    });

    return responseData;
  } catch (error) {
    // all cases throw the error
    const { response } = error;

    let errorAction = null;

    if (response) {
      const { status } = response;

      if (
        (typeof trackErrorsInSentry === 'boolean' && trackErrorsInSentry) ||
        (typeof trackErrorsInSentry === 'function' && trackErrorsInSentry(response))
      ) {
        sendApiSentryError(path, status, method, response);
      }

      if (status >= 500) {
        errorAction = ERROR_ACTION.SHOW_ERROR_MODAL;
      } else if (status === 403 || status === 401) {
        errorAction = ERROR_ACTION.SHOW_LOGOUT_ERROR;
      } else if (status === 400) {
        errorAction = null;
      }
    }

    setError?.({ errorAction, hasError: true });
    throw error;
  }
}

export async function uploadFile(file, fileId, bucketType, storeName) {
  const formData = new FormData();
  const { data } = await apiRequest({
    path: 'internal/s3-form-values',
    params: { bucketType, storeName },
    method: 'POST',
  });
  const s3Data = omit(data, ['access_url', 'action']);

  s3Data.file = file;
  // Build out the form data for the file upload
  // API policy required a Content-Type property set first
  formData.append('Content-Type', 'application/octet-stream');

  forOwn(s3Data, (value, key) => {
    formData.append(key, value);
  });

  const result = await fetch(data.access_url, {
    method: 'POST',
    headers: {},
    body: formData,
  });

  if (result.ok) {
    // Build out response data with all needed properties
    s3Data.access_url = data.access_url;
    s3Data.fileId = fileId;
    // API response uses this template format so disable lint rule
    // eslint-disable-next-line no-template-curly-in-string
    s3Data.relativePath = s3Data.key.replace('${filename}', s3Data.file.name);
    s3Data.link = encodeURI(`${s3Data.access_url}/${s3Data.relativePath}`);

    return s3Data;
  }

  throw result;
}

/**
 * Make a request with XMLHttpRequest and use setProgress callback to update progress
 * @param {string} url - request URL
 * @param {string} method - request method
 * @param {object} body - request body
 * @param {function} setProgress - callback for setting progress status & percent
 */
export async function fetchWithProgress(url, method, body, setProgress) {
  return new Promise(resolve => {
    const xhr = new XMLHttpRequest();
    let percentCompleted = 0;

    xhr.open(method, url);

    // upload progress event
    xhr.upload.addEventListener('progress', e => {
      percentCompleted = (e.loaded / e.total) * 100;
      if (setProgress) {
        setProgress({
          status: 'loading',
          percent: percentCompleted,
        });
      }
    });

    // request finished event
    xhr.addEventListener('load', () => {
      if (setProgress) {
        setProgress({
          status: 'complete',
          percent: percentCompleted,
        });
      }

      resolve(xhr.response.body || {});
    });

    // send POST request to server
    xhr.send(body);
  });
}

export const ADMIN_PRIVILEGES_REQUIRED = '25005';
